/**
 * Cinematic Presets
 *
 * Built-in cinematic event handlers.
 * These are registered by default and can be used in cinematic sequences.
 */

import { Vector3 } from '@babylonjs/core';
import type { CinematicHookFn } from '../types/cinematic';
import { CinematicHookRegistry } from '../hooks/CinematicHookRegistry';
import { logger } from '../../core/Logger';

/**
 * Spawn an entity at a position
 * Params: { behaviorId: string, position: [x, y, z] }
 */
const spawnEntityHandler: CinematicHookFn = (api, params) => {
  const behaviorId = params.behaviorId as string;
  const posArray = params.position as number[];

  if (!behaviorId || !posArray) {
    logger.core.warn('cinematic:spawn_entity: Missing behaviorId or position');
    return;
  }

  const position = new Vector3(posArray[0], posArray[1], posArray[2]);
  api.actions.spawnEntity(behaviorId, position);
};

/**
 * Play a sound effect
 * Params: { soundId: string, volume?: number }
 */
const playSoundHandler: CinematicHookFn = (api, params) => {
  const soundId = params.soundId as string;
  const volume = params.volume as number | undefined;

  if (!soundId) {
    logger.core.warn('cinematic:play_sound: Missing soundId');
    return;
  }

  api.actions.playSound(soundId, { volume });
};

/**
 * Trigger another trigger behavior
 * Params: { triggerId: string }
 */
const triggerHandler: CinematicHookFn = (api, params) => {
  const triggerId = params.triggerId as string;

  if (!triggerId) {
    logger.core.warn('cinematic:trigger: Missing triggerId');
    return;
  }

  api.actions.triggerBehavior(triggerId);
};

/**
 * Fade screen to/from black
 * Params: { alpha: number, duration: number }
 */
const fadeHandler: CinematicHookFn = (api, params) => {
  const alpha = (params.alpha as number) ?? 1;
  const duration = (params.duration as number) ?? 1;

  api.actions.fadeScreen(alpha, duration);
};

/**
 * Enable player input (usually at end of cinematic)
 * Params: none
 */
const enableInputHandler: CinematicHookFn = (api, _params) => {
  api.actions.setPlayerInput(true);
};

/**
 * Disable player input (usually at start of cinematic)
 * Params: none
 */
const disableInputHandler: CinematicHookFn = (api, _params) => {
  api.actions.setPlayerInput(false);
};

/**
 * Emit a custom event
 * Params: { eventName: string, data?: any }
 */
const emitEventHandler: CinematicHookFn = (api, params) => {
  const eventName = params.eventName as string;
  const data = params.data;

  if (!eventName) {
    logger.core.warn('cinematic:emit: Missing eventName');
    return;
  }

  api.actions.emit(eventName, data);
};

/**
 * Log a message (for debugging cinematics)
 * Params: { message: string }
 */
const logHandler: CinematicHookFn = (_api, params) => {
  const message = params.message as string;
  logger.core.info(`[Cinematic] ${message}`);
};

/**
 * Register all built-in cinematic presets
 */
export function registerCinematicPresets(): void {
  CinematicHookRegistry.register('cinematic:spawn_entity', 'Spawn Entity', spawnEntityHandler);
  CinematicHookRegistry.register('cinematic:play_sound', 'Play Sound', playSoundHandler);
  CinematicHookRegistry.register('cinematic:trigger', 'Trigger Behavior', triggerHandler);
  CinematicHookRegistry.register('cinematic:fade', 'Fade Screen', fadeHandler);
  CinematicHookRegistry.register('cinematic:enable_input', 'Enable Input', enableInputHandler);
  CinematicHookRegistry.register('cinematic:disable_input', 'Disable Input', disableInputHandler);
  CinematicHookRegistry.register('cinematic:emit', 'Emit Event', emitEventHandler);
  CinematicHookRegistry.register('cinematic:log', 'Log Message', logHandler);
}
